import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';

const VerificationPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  // Get email from location state (passed from login page)
  const email = location.state?.email || '';

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Verification';
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await authService.verifyCode(email, data.verificationCode);
      toast.success('Verification successful!');
      navigate('/auth/role-selection');
    } catch (error) {
      toast.error(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await authService.resendVerificationCode(email);
      toast.success('Verification code sent to your email!');
      setCountdown(60); // 60 seconds countdown
    } catch (error) {
      toast.error(error.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Redirect if no email is provided
  useEffect(() => {
    if (!email) {
      toast.error('Please login first');
      navigate('/auth/login');
    }
  }, [email, navigate]);

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Full Screen Image (45%) */}
      <div className="w-[45%] relative overflow-hidden h-[115vh]">
        <img
          src="/login_side_image.png"
          alt="Healthcare Professional"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right Side - Verification Form (55%) */}
      <div className="w-[55%] flex items-start justify-center bg-white pt-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xs w-full space-y-6">
          {/* Header */}
          <div className="text-center">
            {/* Medi Fusion Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/login_medi_logo.png"
                alt="Medi Fusion"
                className="h-16 w-auto"
              />
            </div>
            
            {/* Instructional Text */}
            <div className="text-sm text-gray-700 space-y-3 mb-6">
              <p>
                You're trying to Log In to Medi Fusion. To make sure your account is secure, we have to verify your identity.
              </p>
              <p>
                A verification code has been sent to your email. Please check your email and enter the 6-digit code below.
              </p>
            </div>
          </div>

          {/* Verification Form */}
          <div className="space-y-6">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Verification Code Field */}
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code:
                </label>
                <input
                  id="verificationCode"
                  type="text"
                  maxLength="6"
                  {...register('verificationCode', {
                    required: 'Verification code is required',
                    minLength: {
                      value: 6,
                      message: 'Verification code must be 6 digits',
                    },
                    maxLength: {
                      value: 6,
                      message: 'Verification code must be 6 digits',
                    },
                    pattern: {
                      value: /^\d{6}$/,
                      message: 'Please enter a valid 6-digit code',
                    },
                  })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
                  placeholder="000000"
                />
                {errors.verificationCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.verificationCode.message}</p>
                )}
              </div>

              {/* Verify Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Verify'
                )}
              </button>
            </form>

            {/* Resend Code Section */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                Didn't receive the code?
              </p>
              {countdown > 0 ? (
                <p className="text-sm text-gray-500">
                  Resend available in {countdown} seconds
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResending}
                  className="text-blue-600 hover:text-blue-500 text-sm font-medium disabled:opacity-50"
                >
                  {isResending ? 'Sending...' : 'Resend Code'}
                </button>
              )}
            </div>

            {/* Back to Login */}
            <div className="text-center pt-4 border-t border-gray-200">
              <Link
                to="/auth/login"
                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                ← Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage; 